
import { useState, useEffect, useRef, useCallback } from 'react';
import { createClient, RealtimeChannel } from '@supabase/supabase-js';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import StatsCards from './components/StatsCards';
import OrdersTable from './components/OrdersTable';
import RevenueChart from './components/RevenueChart';

interface Order {
  id: string;
  created_at: string;
  stripe_session_id: string;
  stripe_payment_intent: string;
  product_id: string;
  product_name: string;
  product_brand: string;
  product_category: string;
  product_price: number;
  commission_rate: number;
  commission_amount: number;
  customer_email: string;
  customer_name: string;
  payment_status: string;
  order_status: string;
  shipping_address?: {
    city?: string;
    country?: string;
    line1?: string;
    state?: string;
    postal_code?: string;
  };
  metadata?: Record<string, unknown>;
}

type ErrorType = 'not_configured' | 'fetch_failed' | 'unknown' | null;

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;

export default function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ErrorType>(null);
  const [isLive, setIsLive] = useState(false);
  const [newOrderAlert, setNewOrderAlert] = useState<Order | null>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const alertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const supabase = supabaseUrl && supabaseKey
    ? createClient(supabaseUrl, supabaseKey)
    : null;

  const loadOrders = useCallback(async () => {
    if (!supabase) {
      setError('not_configured');
      setLoading(false);
      return;
    }
    try {
      const { data, error: fetchError } = await supabase
        .from('orders')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        setError('fetch_failed');
        setLoading(false);
        return;
      }
      setOrders(data || []);
      setError(null);
    } catch {
      setError('unknown');
    } finally {
      setLoading(false);
    }
  }, [supabase]);

  // Subscribe to real-time changes
  useEffect(() => {
    if (!supabase) return;

    loadOrders();

    const channel = supabase
      .channel('orders-realtime')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'orders' },
        (payload) => {
          const newOrder = payload.new as Order;
          setOrders(prev => [newOrder, ...prev]);
          setNewOrderAlert(newOrder);
          if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
          alertTimerRef.current = setTimeout(() => setNewOrderAlert(null), 5000);
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'orders' },
        (payload) => {
          const updated = payload.new as Order;
          setOrders(prev => prev.map(o => o.id === updated.id ? updated : o));
        }
      )
      .subscribe((status) => {
        setIsLive(status === 'SUBSCRIBED');
      });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      if (alertTimerRef.current) clearTimeout(alertTimerRef.current);
    };
  }, [supabase, loadOrders]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">Loading orders dashboard...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error === 'not_configured') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
          <div className="max-w-lg mx-auto px-6 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100">
              <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-database-2-line text-3xl text-teal-500"></i>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">Database Not Configured</h2>
              <p className="text-gray-500 text-sm leading-relaxed">
                Connect Supabase to enable real-time order tracking, commission analytics, and live dashboards.
              </p>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  if (error === 'fetch_failed' || error === 'unknown') {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
          <div className="max-w-md mx-auto px-6 text-center">
            <div className="bg-white rounded-2xl p-12 shadow-lg border border-gray-100">
              <i className="ri-error-warning-line text-5xl text-red-400 mb-4"></i>
              <h2 className="text-xl font-bold text-gray-900 mb-3">Connection Error</h2>
              <p className="text-gray-500 text-sm mb-6">Unable to load orders. Please check your connection and try again.</p>
              <button
                onClick={loadOrders}
                className="px-6 py-2.5 bg-teal-500 text-white rounded-lg text-sm font-medium hover:bg-teal-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-10">
        <div className="max-w-7xl mx-auto px-6">

          {/* Header */}
          <div className="flex items-start justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-1">Order Dashboard</h1>
              <p className="text-gray-500 text-sm">Real-time tracking of all orders and commission earnings</p>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <div className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full border ${isLive ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'}`}></span>
                {isLive ? 'Live Updates Active' : 'Connecting...'}
              </div>
            </div>
          </div>

          {/* New Order Toast */}
          {newOrderAlert && (
            <div className="fixed top-6 right-6 z-50 bg-white border border-emerald-200 rounded-xl shadow-xl p-4 flex items-start gap-3 max-w-sm animate-bounce-once">
              <div className="w-9 h-9 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-shopping-bag-3-fill text-emerald-600"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-gray-900">New Order Received!</div>
                <div className="text-xs text-gray-500 truncate">{newOrderAlert.product_name}</div>
                <div className="text-xs text-emerald-600 font-medium">${Number(newOrderAlert.product_price).toFixed(2)}</div>
              </div>
              <button onClick={() => setNewOrderAlert(null)} className="text-gray-400 hover:text-gray-600 cursor-pointer flex-shrink-0">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
          )}

          {/* Stats */}
          <StatsCards orders={orders} />

          {/* Charts */}
          <RevenueChart orders={orders} />

          {/* Orders Table */}
          <OrdersTable orders={orders} onRefresh={loadOrders} isLive={isLive} />

          {/* Footer Banner */}
          <div className="mt-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl p-7 flex items-center gap-5">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-2xl text-white"></i>
            </div>
            <div>
              <h3 className="text-white font-semibold text-lg mb-0.5">Automated Commission Tracking</h3>
              <p className="text-teal-50 text-sm leading-relaxed">
                All orders are tracked in real-time with commissions calculated automatically at 15% per sale.
                Payments are processed securely via Stripe and synced instantly to this dashboard.
              </p>
            </div>
          </div>

        </div>
      </div>
      <Footer />
    </>
  );
}
